import { Box, Button, Divider, Paper, Stack, Tab, Tabs } from "@mui/material"
import { useEffect, useMemo, useRef, useState } from "react"
import { PageBreadcrumbContainer } from "lib/page-breadcrumb-container"
import { SearchBoxUsingSearchParam } from "lib/@components/searchBoxUsingSearchParam"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { StandardSwitch } from "lib/@components/binding/bound-components"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import Iconify from "minimals-template/components/Iconify"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { getMacros } from "authoring/routes/macro/controller/get-macros"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { NoResults } from "lib/@components/no-results/no-results"
import { MacroList } from "./components/macro-list"
import { MacroBinList } from "./components/macro-bin-list"
import { MacroEditorPanel } from "./components/macro-editor-panel"
import { splitMacros } from "./lib/macro-helpers"
import { MacroCategoryFilters } from "./components/macro-category-filters"
import { buildMacroCategoryList, filterMacrosByCategories } from "./lib/macro-category-utils"
import { useCreateMacroDialog } from "./hooks/use-create-macro-dialog"
import { MacroUpdated } from "./macro-events"
import { useDemand } from "lib/authorization/use-demand"
import { Secure } from "lib/authorization/secure"
import { Frag } from "lib/@components/slot/frag"
import { AUTHORING_MANAGE_MACROS_DEMAND } from "authoring/lib/constants/authoring-permissions.mjs"

const MACRO_VIEW = {
    MACROS: "macros",
    BIN: "bin",
}

export function MacrosPage() {
    const [view, setView] = useState(MACRO_VIEW.MACROS)
    const [selectedId, setSelectedId] = useState(null)
    const canManageMacros = useDemand(AUTHORING_MANAGE_MACROS_DEMAND)
    const [editMode, setEditMode] = useState(canManageMacros)
    const effectiveEditMode = editMode && canManageMacros
    const [selectedCategories, setSelectedCategories] = useState([])
    const [search] = useSearchParam("search", "")
    const selectedIdRef = useRef(selectedId)

    const [macros, refreshMacros] = getMacros.useResults.withRefetch(
        { retrieveDeleted: true },
        withInitialValue(IS_LOADING)
    ) ?? [IS_LOADING]
    const [macroVersion, setMacroVersion] = useState(0)
    MacroUpdated.useEvent(bumpMacroVersion, [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const allMacros = useMemo(computeAllMacros, [macros, macroVersion])
    const { activeMacros, deletedMacros } = useMemo(() => splitMacros(allMacros), [allMacros])
    const categoryOptions = useMemo(() => buildMacroCategoryList(allMacros), [allMacros])
    const { openCreateMacro } = useCreateMacroDialog({ categories: categoryOptions, onCreated: handleMacroCreated })

    const searchValue = normalizeSearch(search)
    const filteredActive = useMemo(
        () => filterMacros(activeMacros, searchValue, selectedCategories),
        [activeMacros, searchValue, selectedCategories]
    )
    const filteredDeleted = useMemo(
        () => filterMacros(deletedMacros, searchValue, selectedCategories),
        [deletedMacros, searchValue, selectedCategories]
    )
    selectedIdRef.current = selectedId
    const selectedMacro = useMemo(() => findMacroById(activeMacros, selectedId), [activeMacros, selectedId])
    useEffect(() => {
        if (!canManageMacros) setEditMode(false)
    }, [canManageMacros])
    useEffect(() => {
        if (view === MACRO_VIEW.BIN) {
            if (selectedIdRef.current) {
                setSelectedId(null)
            }
            return
        }
        const nextSelected = resolveSelectedId(filteredActive, selectedIdRef.current)
        if (nextSelected !== selectedIdRef.current) {
            setSelectedId(nextSelected)
        }
    }, [filteredActive, view])

    if (macros === IS_LOADING) return <LoadingScreen />

    return (
        <PageBreadcrumbContainer title="Macros" description="Reusable snippets for schedule authoring" hideCrumbs>
            <Stack spacing={2} direction="row" sx={{ width: 1, flex: 1, minHeight: 0 }}>
                <Paper
                    sx={{
                        width: { xs: 1, md: 420 },
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 0,
                    }}
                >
                    <Box sx={{ px: 1.5, pt: 1.5, pb: 0.75 }}>
                        <SearchBoxUsingSearchParam
                            autoFocus={false}
                            placeholder="Search macros"
                            variant="outlined"
                            textFieldProps={{ size: "small" }}
                            py={0}
                            m={0}
                        />
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", borderBottom: 1, borderColor: "divider" }}>
                        <Tabs value={view} onChange={handleViewChange} sx={{ minHeight: 40 }}>
                            <Tab
                                label="Macros"
                                value={MACRO_VIEW.MACROS}
                                sx={{ minHeight: 40, py: 0 }}
                                data-cy="macro-list-tab"
                            />
                            <Tab
                                label="Bin"
                                value={MACRO_VIEW.BIN}
                                sx={{ minHeight: 40, py: 0 }}
                                data-cy="macro-bin-tab"
                            />
                        </Tabs>
                        <Box flex={1} />
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ pr: 1.5 }}
                            data-cy="macro-page-controls"
                        >
                            <If truthy={view === MACRO_VIEW.MACROS && canManageMacros}>
                                <StandardSwitch
                                    value={editMode}
                                    onChange={setEditMode}
                                    label="Edit mode"
                                    size="small"
                                    inputProps={{ "data-cy": "macro-edit-mode-toggle" }}
                                />
                            </If>
                            <Secure demand={AUTHORING_MANAGE_MACROS_DEMAND} fallback={<Frag />}>
                                <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<Iconify icon="ic:round-add" />}
                                    onClick={handleCreateMacro}
                                    data-cy="macro-create-button"
                                >
                                    Create
                                </Button>
                            </Secure>
                        </Stack>
                    </Box>
                    <If truthy={categoryOptions.length > 0}>
                        <Box sx={{ px: 1.5, pt: 1.5, pb: 0.75 }} data-cy="macro-category-filters">
                            <MacroCategoryFilters
                                categories={categoryOptions}
                                selectedCategories={selectedCategories}
                                onChangeCategories={setSelectedCategories}
                            />
                        </Box>
                    </If>
                    <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                        <If truthy={view === MACRO_VIEW.MACROS}>
                            <MacroList
                                macros={filteredActive}
                                totalCount={activeMacros.length}
                                selectedId={selectedId}
                                onSelect={setSelectedId}
                                onCreate={canManageMacros ? handleCreateMacro : undefined}
                            />

                            <Else>
                                <MacroBinList macros={filteredDeleted} />
                            </Else>
                        </If>
                    </Box>
                </Paper>

                <Divider orientation="vertical" flexItem sx={{ borderColor: "divider", mt: 1 }} />

                <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
                    <If truthy={view === MACRO_VIEW.MACROS}>
                        <If truthy={selectedMacro}>
                            <MacroEditorPanel
                                macro={selectedMacro}
                                macros={allMacros}
                                editMode={effectiveEditMode}
                                onArchive={handleArchive}
                            />
                            <Else>
                                <Stack sx={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
                                    <NoResults mt={0} width={200}>
                                        Select a macro to view or edit.
                                    </NoResults>
                                </Stack>
                            </Else>
                        </If>
                    </If>
                </Box>
            </Stack>
        </PageBreadcrumbContainer>
    )

    function handleViewChange(_event, value) {
        setView(value)
    }

    function handleCreateMacro() {
        void openCreateMacro()
    }

    function handleArchive() {
        setSelectedId(null)
    }

    function handleMacroCreated(created) {
        if (!created) return
        refreshMacros()
        setView(MACRO_VIEW.MACROS)
        setSelectedId(created.id)
    }

    function bumpMacroVersion() {
        setMacroVersion((v) => v + 1)
    }

    function computeAllMacros() {
        if (!macros || macros === IS_LOADING) return []
        return [...macros]
    }
}

function normalizeSearch(value = "") {
    return value.trim().toLowerCase()
}

function filterMacros(macros = [], searchValue = "", selectedCategories = []) {
    const searched = filterMacrosBySearch(macros, searchValue)
    return filterMacrosByCategories(searched, selectedCategories)
}

function filterMacrosBySearch(macros = [], searchValue = "") {
    if (!searchValue) return macros
    return macros.filter((macro) => {
        const name = macro?.name?.toLowerCase() ?? ""
        const id = macro?.id?.toLowerCase() ?? ""
        return name.includes(searchValue) || id.includes(searchValue)
    })
}

function findMacroById(macros = [], id) {
    if (!id) return null
    return macros.find((macro) => macro.id === id) ?? null
}

function resolveSelectedId(list = [], selectedId) {
    if (!list.length) return null
    const exists = list.some(({ id }) => id === selectedId)
    if (exists) return selectedId
    return list[0]?.id ?? null
}
